import type { AxiosRequestConfig } from 'axios';
import urlcat from 'urlcat';
import type {
    DisplayMediaBreakdownData,
    DisplayMediaBreakdownPayload,
    HttpProgressUpdate,
    ListSearchOptions,
    ModuleConstructorProps,
    PopDisplays_Display,
    PopDisplaysPlayoutExportPayload,
} from '../Types';
import { generateArrayQSParams } from '../Lib';
import { ListEndpointFunc } from './BaseEntityModuleFuncs';

const PopDisplays = ({ endpoints, httpClient }: ModuleConstructorProps) => ({
    playoutExport: (data: PopDisplaysPlayoutExportPayload, config?: AxiosRequestConfig) =>
        httpClient.post<{ success: boolean; message: string }>(
            endpoints.popDisplays.playoutExport,
            data,
            config,
        ),
    channelFrameMediaBreakdown: (
        channelFrameId: number,
        data?: DisplayMediaBreakdownPayload,
        config?: AxiosRequestConfig,
    ) =>
        httpClient.post<{ data: DisplayMediaBreakdownData[] }>(
            urlcat(endpoints.Entities.PopDisplays_ChannelFrame.mediaBreakdown, {
                id: channelFrameId,
            }),
            data,
            config,
        ),
    channelFrameExport: (data?: ListSearchOptions, config?: AxiosRequestConfig) =>
        ListEndpointFunc<Blob>(httpClient)(
            { endpoint: endpoints.Entities.PopDisplays_ChannelFrame.export, options: data },
            config,
        ),
    displayExport: (data?: ListSearchOptions, config?: AxiosRequestConfig) =>
        ListEndpointFunc<{ success: boolean; message: string }>(httpClient)(
            { endpoint: endpoints.Entities.PopDisplays_Display.export, options: data },
            config,
        ),
    addDisplayPortalId: (
        id: string | number,
        display_portal_ids: string[] | number[],
        config?: AxiosRequestConfig,
    ) =>
        httpClient.post<{ message: string; success: boolean }>(
            urlcat(endpoints.Entities.PopDisplays_ChannelFrame.addDisplayPortalId, {
                id: String(id),
            }),
            { display_portal_ids },
            config,
        ),
    removeDisplayPortalId: (
        id: string | number,
        display_portal_ids: string[] | number[],
        config?: AxiosRequestConfig,
    ) =>
        httpClient.post<{ message: string; success: boolean }>(
            urlcat(endpoints.Entities.PopDisplays_ChannelFrame.removeDisplayPortalId, {
                id: String(id),
            }),
            { display_portal_ids },
            config,
        ),
    /**
     * file: File - the actual file to upload
     */
    channelFrameImport: (
        { formData, onProgress }: { formData; onProgress?: HttpProgressUpdate },
        config?: AxiosRequestConfig,
    ) =>
        httpClient.postFile<{ success: boolean; message: string }>(
            endpoints.Entities.PopDisplays_ChannelFrame.import,
            formData,
            onProgress,
            {},
            config,
        ),
    attachDisplayChannelFrames: (
        displayId: number,
        channelFrameIds: number[],
        includes?: string[],
        config?: AxiosRequestConfig,
    ) =>
        httpClient.post<{ data: PopDisplays_Display }>(
            `${urlcat(endpoints.Entities.PopDisplays_Display.attachChannelFrames, {
                id: displayId,
            })}${generateArrayQSParams(includes, 'include', '?')}`,
            { channel_frame_ids: channelFrameIds },
            config,
        ),
    detachDisplayChannelFrames: (
        displayId: number,
        channelFrameIds: number[],
        includes?: string[],
        config?: AxiosRequestConfig,
    ) => {
        const includesString = generateArrayQSParams(includes, 'include', '?');

        return httpClient.delete<{ data: PopDisplays_Display }>(
            `${urlcat(endpoints.Entities.PopDisplays_Display.detachChannelFrames, {
                id: displayId,
            })}${generateArrayQSParams(includes, 'include', '?')}`,
            { channel_frame_ids: channelFrameIds },
            config,
        );
    },
    logUpload: (formData: FormData, onProgress?: HttpProgressUpdate, config?: AxiosRequestConfig) =>
        httpClient.postFile<{ success: boolean; message: string }>(
            endpoints.Entities.PopDisplays_LogFileImport.upload,
            formData,
            onProgress,
            {},
            config,
        ),
    logChangeStatus: (
        type: 'reprocess' | 'delete' | 'ignore',
        log_file_import_id: number[],
        config?: AxiosRequestConfig,
    ) =>
        httpClient.post(
            endpoints.Entities.PopDisplays_LogFileImport.changeStatus,
            {
                type,
                log_file_import_id,
            },
            config,
        ),
    classicLogUpload: (
        formData: FormData,
        onProgress?: HttpProgressUpdate,
        config?: AxiosRequestConfig,
    ) =>
        httpClient.postFile<{ success: boolean; message: string }>(
            endpoints.Entities.PopDisplays_LogFileImport.classicUpload,
            formData,
            onProgress,
            {},
            config,
        ),
});

export default PopDisplays;
